import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { Toaster } from "./components/ui/sonner";
import { useEffect } from "react";
import { getStoreBySubdomain, getStoreByCustomDomain } from "./lib/firebaseService";
import Landing from "./pages/Landing";
import Login from "./pages/Login";
import Register from "./pages/Register";
import Dashboard from "./pages/Dashboard";
import Products from "./pages/Products";
import Orders from "./pages/Orders";
import StoreInfo from "./pages/StoreInfo";
import StoreLogo from "./pages/StoreLogo";
import StoreAbout from "./pages/StoreAbout";
import StoreLink from "./pages/StoreLink";
import StoreManagement from "./pages/StoreManagement";
import StoreBanner from "./pages/StoreBanner";
import StoreSocial from "./pages/StoreSocial";
import StoreTheme from "./pages/StoreTheme";
import Settings from "./pages/Settings";
import PublicStore from "./pages/PublicStore";
import ProductDetail from "./pages/ProductDetail";
import ProductPage from "./pages/ProductPage";
import StoresPage from "./pages/StoresPage";
import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminUsers from "./pages/admin/AdminUsers";
import AdminStores from "./pages/admin/AdminStores";
import AdminPlans from "./pages/admin/AdminPlans";
import AdminAbout from "./pages/admin/AdminAbout";
import AdminPartners from "./pages/admin/AdminPartners";
import AdminReviews from "./pages/admin/AdminReviews";

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-lg text-muted-foreground">Yüklənir...</div>
      </div>
    );
  }
  
  return user ? children : <Navigate to="/login" replace />;
}

function AppRoutes() {
  const navigate = useNavigate();

  useEffect(() => {
    checkSubdomain();
  }, []);

  const checkSubdomain = async () => {
    const hostname = window.location.hostname;
    const parts = hostname.split('.');
    
    // Əvvəlcə custom domain yoxla (məs: mystore.az)
    // localhost, preview URL-ləri və əsas domain-i istisna et
    const isLocalhost = hostname.includes('localhost');
    const isPreview = hostname.includes('preview.emergentagent.com');
    const isMainDomain = hostname === 'iveb.az' || hostname === 'www.iveb.az';
    
    if (!isLocalhost && !isPreview && !isMainDomain) {
      try {
        // Custom domain ilə mağaza axtar
        const store = await getStoreByCustomDomain(hostname);
        
        if (store) {
          // Custom domain tapıldı, mağaza səhifəsinə yönləndir
          navigate(`/store/${store.id}`, { replace: true });
          return;
        }
      } catch (error) {
        console.error('Custom domain yoxlama xətası:', error);
      }
    }
    
    // Subdomain yoxlaması (minimum 3 part: subdomain.iveb.az)
    // localhost və www istisna edilir
    if (parts.length >= 3 && parts[0] !== 'www' && !hostname.includes('localhost')) {
      const subdomain = parts[0];
      
      // Rezerv subdomain-ləri yoxla
      const reserved = ['admin', 'api', 'app', 'mail', 'ftp', 'test', 'dev', 'staging', 'ecom-platform-148'];
      if (reserved.includes(subdomain)) {
        return; // Rezerv subdomain, normal routing
      }
      
      try {
        // Subdomain ilə mağaza tap
        const store = await getStoreBySubdomain(subdomain);
        
        if (store) {
          // Subdomain tapıldı, mağaza səhifəsinə yönləndir
          navigate(`/store/${store.id}`, { replace: true });
        }
      } catch (error) {
        console.error('Subdomain yoxlama xətası:', error);
      }
    }
  };

  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/stores" element={<StoresPage />} />
      <Route path="/store/:storeId" element={<PublicStore />} />
      <Route path="/product/:productId" element={<ProductDetail />} />
      
      <Route
        path="/admin"
        element={
          <ProtectedRoute>
            <AdminDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/users"
        element={
          <ProtectedRoute>
            <AdminUsers />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/stores"
        element={
          <ProtectedRoute>
            <AdminStores />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/plans"
        element={
          <ProtectedRoute>
            <AdminPlans />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/about"
        element={
          <ProtectedRoute>
            <AdminAbout />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/partners"
        element={
          <ProtectedRoute>
            <AdminPartners />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/about"
        element={
          <ProtectedRoute>
            <AdminAbout />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/partners"
        element={
          <ProtectedRoute>
            <AdminPartners />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/reviews"
        element={
          <ProtectedRoute>
            <AdminReviews />
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/products"
        element={
          <ProtectedRoute>
            <Products />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/orders"
        element={
          <ProtectedRoute>
            <Orders />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/store/info"
        element={
          <ProtectedRoute>
            <StoreInfo />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/store/logo"
        element={
          <ProtectedRoute>
            <StoreLogo />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/store/about"
        element={
          <ProtectedRoute>
            <StoreAbout />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/store/link"
        element={
          <ProtectedRoute>
            <StoreLink />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/store/management"
        element={
          <ProtectedRoute>
            <StoreManagement />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/store/banner"
        element={
          <ProtectedRoute>
            <StoreBanner />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/store/social"
        element={
          <ProtectedRoute>
            <StoreSocial />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/store/theme"
        element={
          <ProtectedRoute>
            <StoreTheme />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/settings"
        element={
          <ProtectedRoute>
            <Settings />
          </ProtectedRoute>
        }
      />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
        <Toaster position="top-right" />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;